import { CircleHelp } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { CopyValueButton } from "./ui";

const glossary = [
  [
    "PDL",
    "Point de livraison : vue métier d’un compteur dans une marque et un site.",
  ],
  ["PRM", "Point de référence et de mesure utilisé pour l’électricité."],
  ["PCE", "Point de comptage et d’estimation utilisé pour le gaz."],
  [
    "DJU",
    "Degrés-jours unifiés utilisés pour rapprocher énergie et conditions météorologiques.",
  ],
  [
    "Talon",
    "Puissance minimale appelée de manière durable, hors pointes ponctuelles.",
  ],
  [
    "Correction",
    "Révision tracée d’une valeur ; la mesure d’origine n’est pas écrasée.",
  ],
  [
    "Estimation",
    "Valeur fournie ou calculée avec une incertitude explicite ; ce n’est pas une mesure brute.",
  ],
] as const;

export function ContextHelp(): React.ReactNode {
  const { pathname } = useLocation();
  const helpPath = pathname.replace(
    /^\/app\/(company|delivery-point)\/[^/]+/,
    "/app/$1/:identifiant",
  );
  const ref = useRef<HTMLDetailsElement>(null);
  const [includeScreen, setIncludeScreen] = useState(false);
  const guide = pathname.includes("reports")
    ? "Rapports : préparez un aperçu, figez une version, puis approuvez-la avant diffusion. Une programmation prépare un brouillon, jamais un envoi automatique."
    : pathname.includes("dashboard")
      ? "Tableau de bord : choisissez un modèle puis personnalisez vos widgets. Le détail reprend le résultat enregistré ; les sites et périodes spécifiques restent indiqués."
      : pathname.includes("workflow")
        ? "Suivi : qualifiez le constat, attribuez un responsable et une échéance. Les commentaires internes restent privés ; relisez l’aperçu client avant publication."
        : pathname.includes("admin/portfolio")
          ? "Référentiel : créez l’entreprise, puis le site et ses points. Vérifiez les suggestions d’adresse et les doublons. Les imports proposent un aperçu avant toute écriture."
          : "Les valeurs énergétiques indiquent leur source, leur période et leurs limites directement dans les écrans concernés. Utilisez la recherche pour rejoindre un objet de votre périmètre.";
  useEffect(() => {
    const menu = ref.current;
    if (!menu) return;
    const dismiss = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || !menu.open) return;
      event.preventDefault();
      menu.open = false;
      menu.querySelector("summary")?.focus();
    };
    menu.addEventListener("keydown", dismiss);
    return () => menu.removeEventListener("keydown", dismiss);
  }, []);
  return (
    <details className="context-help" ref={ref}>
      <summary className="icon-button" aria-label="Aide et glossaire">
        <CircleHelp size={18} />
      </summary>
      <div className="context-help-popover">
        <h2>Aide et glossaire</h2>
        <p>{guide}</p>
        <dl>
          {glossary.map(([term, definition]) => (
            <div key={term}>
              <dt>{term}</dt>
              <dd>{definition}</dd>
            </div>
          ))}
        </dl>
        <p className="context-help-note">
          Ces définitions sont pédagogiques et ne constituent pas des données du
          site sélectionné. Glossaire version 1 — 18 septembre 2026.
        </p>
        <h2>Préparer une demande d’aide</h2>
        <p>
          Copiez ce modèle pour votre interlocuteur habituel. Aucun envoi
          automatique, nom de site, identifiant ou donnée énergétique n’est
          inclus.
        </p>
        <label>
          <input
            type="checkbox"
            checked={includeScreen}
            onChange={(event) => setIncludeScreen(event.target.checked)}
          />{" "}
          Inclure le chemin de cet écran, sans paramètres
        </label>
        <CopyValueButton
          label="Copier le modèle de demande d’aide"
          value={`Syllarys EMS — demande d’aide\n${includeScreen ? `Écran : ${helpPath}\n` : ""}Action réalisée :\nRésultat attendu :\nRésultat observé :`}
        />
      </div>
    </details>
  );
}
